import { useState, useEffect, useRef } from 'react'
import { Search, X, Building2, User, Calendar, DollarSign, Loader2 } from 'lucide-react'
import { useHomeStore } from '../store'
import type { HubSpotDeal } from '../types'

export function HubSpotSearch() {
  const { searchQuery, setSearchQuery, searchDeals, searchResults, isSearching, hasSearched, clearSearch, selectDeal, selectedDeal } =
    useHomeStore()
  const [isOpen, setIsOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Debounced search
  useEffect(() => {
    if (searchQuery.length < 2) {
      return
    }

    const timer = setTimeout(() => {
      searchDeals(searchQuery)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, searchDeals])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchQuery(value)
    setIsOpen(value.length >= 2)
  }

  const handleSelectDeal = (deal: HubSpotDeal) => {
    selectDeal(deal)
    setIsOpen(false)
  }

  const handleClear = () => {
    clearSearch()
    setIsOpen(false)
    inputRef.current?.focus()
  }

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return '-'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          {isSearching ? (
            <Loader2 size={20} className="text-[var(--text-muted)] animate-spin" />
          ) : (
            <Search size={20} className="text-[var(--text-muted)]" />
          )}
        </div>
        <input
          ref={inputRef}
          type="text"
          className="w-full h-14 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg pl-12 pr-12 text-[var(--text-primary)] text-lg placeholder-gray-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          placeholder="Search HubSpot deals by name, company, or owner..."
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => searchQuery.length >= 2 && setIsOpen(true)}
        />
        {searchQuery && (
          <button
            className="absolute inset-y-0 right-0 pr-4 flex items-center text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            onClick={handleClear}
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Selected Deal Display */}
      {selectedDeal && (
        <div className="mt-4 bg-primary/10 border border-primary/30 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-primary/80 uppercase tracking-wide">Selected Deal</span>
              </div>
              <h3 className="text-lg font-medium text-[var(--text-primary)]">{selectedDeal.name}</h3>
              <div className="flex items-center gap-4 mt-2 text-sm text-[var(--text-secondary)]">
                {selectedDeal.companyName && (
                  <span className="flex items-center gap-1">
                    <Building2 size={14} />
                    {selectedDeal.companyName}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <User size={14} />
                  {selectedDeal.ownerName}
                </span>
                <span className="flex items-center gap-1">
                  <DollarSign size={14} />
                  {formatCurrency(selectedDeal.amount)}
                </span>
              </div>
            </div>
            <button
              className="text-[var(--text-muted)] hover:text-[var(--text-primary)] p-1"
              onClick={() => selectDeal(null)}
            >
              <X size={18} />
            </button>
          </div>

        </div>
      )}

      {/* Search Results Dropdown */}
      {isOpen && !selectedDeal && (
        <div className="absolute z-50 w-full mt-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-xl max-h-96 overflow-y-auto">
          {isSearching ? (
            <div className="p-8 text-center">
              <Loader2 size={24} className="text-primary animate-spin mx-auto mb-2" />
              <p className="text-sm text-[var(--text-muted)]">Searching HubSpot...</p>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="divide-y divide-dark-border">
              {searchResults.map((deal) => (
                <button
                  key={deal.id}
                  className="w-full p-4 text-left hover:bg-[var(--bg-tertiary)] transition-colors"
                  onClick={() => handleSelectDeal(deal)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-[var(--text-primary)] truncate">{deal.name}</h4>
                      <div className="flex items-center gap-3 mt-1 text-xs text-[var(--text-muted)]">
                        {deal.companyName && (
                          <span className="flex items-center gap-1">
                            <Building2 size={12} />
                            {deal.companyName}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <User size={12} />
                          {deal.ownerName}
                        </span>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-sm font-medium text-[var(--text-primary)]">{formatCurrency(deal.amount)}</div>
                      <div className="flex items-center gap-1 text-xs text-[var(--text-muted)] mt-1">
                        <Calendar size={12} />
                        {formatDate(deal.closeDate)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs ${
                        deal.stage === 'Contract Sent'
                          ? 'bg-success/20 text-success'
                          : deal.stage === 'Negotiation'
                            ? 'bg-warning/20 text-warning'
                            : 'bg-primary/20 text-primary'
                      }`}
                    >
                      {deal.stage}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : hasSearched ? (
            <div className="p-8 text-center">
              <p className="text-[var(--text-muted)]">No deals found matching "{searchQuery}"</p>
              <p className="text-xs text-gray-600 mt-1">Try a different search term</p>
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-[var(--text-muted)]">Type at least 2 characters to search</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
